import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Shield, Users, Lock, Eye, Globe, Database, Mail, AlertCircle } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';

const PrivacyPolicy: React.FC = () => {
  const { theme } = useColorTheme();

  const SectionIcon = ({ icon: Icon, className }: { icon: any; className?: string }) => (
    <Icon className={cn("h-5 w-5", theme.secondary, className)} />
  );

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Shield className="h-4 w-4" />
            <span>Effective Date: June 19, 2025</span>
          </div>
          <p className="mt-4 text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect 
            your information when you use our services that enable one-on-one video calls between fans and public figures.
          </p>
        </div>

        {/* Privacy Content */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          
          {/* Section 1: Who We Are */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Star} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">1. Who We Are</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                Nexbii is a global digital platform that enables personal video calls between fans and influencers. 
                We connect user accounts with Instagram, YouTube, TikTok, and other platforms to verify identities 
                and offer meaningful interactions.
              </p>
            </div>
          </section>

          {/* Section 2: Age Requirements */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">2. Age Requirements</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">To use Nexbii, you must meet the minimum age requirements of major social media platforms:</p>
              <div className={cn("p-4 rounded-lg border-l-4", theme.accent)}>
                <p className="font-medium">
                  <strong>13 years or older</strong>, or the age of digital consent in your country (e.g., 16 in the EU under GDPR)
                </p>
              </div>
              <p className="mt-4">By using Nexbii, you confirm you meet this requirement.</p>
            </div>
          </section>

          {/* Section 3: Information Collection */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Database} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">3. What Information We Collect</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">We collect the following types of information:</p>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">👤 Basic Profile Info</h4>
                  <p className="text-blue-700 text-sm">Name, email address, phone number</p>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">🔗 Connected Account Data</h4>
                  <p className="text-purple-700 text-sm">Public details from Instagram, YouTube, or TikTok including follower count and profile links</p>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">📅 Booking Details</h4>
                  <p className="text-green-700 text-sm">Time slots booked, bid amounts, availability, call status (but not the content of calls)</p>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">💳 Payment Information</h4>
                  <p className="text-yellow-700 text-sm">Handled securely by our partner Creem; we do not store full card details</p>
                </div>
              </div>
              
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">📱 Device & Technical Info</h4>
                <p className="text-gray-700 text-sm">IP address, app version, device type, and crash logs</p>
              </div>
            </div>
          </section>

          {/* Section 4: How We Use Information */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Shield} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">4. How We Use Your Information</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Create and manage your account</li>
                <li>Schedule and manage video call bookings</li>
                <li>Verify your influencer status via connected platforms</li>
                <li>Process payments through our partner (Creem)</li>
                <li>Prevent abuse or misuse of the platform (e.g., banning users for violations)</li>
                <li>Improve our services and understand usage patterns through analytics tools such as Firebase Analytics</li>
              </ul>
            </div>
          </section>

          {/* Section 5: Video Calls */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Eye} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">5. Video Calls</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <ul className="list-disc list-inside space-y-3">
                <li>All video calls are hosted by <strong>Daily.co</strong>, a third-party provider.</li>
                <li>
                  <strong>We do not record or store any video or audio content</strong> from your calls.
                </li>
              </ul>
              <div className={cn("mt-4 p-4 rounded-lg border-l-4", "bg-blue-50 border-blue-400")}>
                <p className="font-medium text-blue-800">
                  Future Feature: If we introduce call recordings in the future, we will ask for your explicit consent before doing so.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Data Sharing */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">6. Sharing of Your Data</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className={cn("p-4 rounded-lg border-l-4 mb-4", "bg-green-50 border-green-400")}>
                <p className="font-medium text-green-800">
                  ✅ We do not sell or rent your personal data.
                </p>
              </div>
              
              <p className="mb-4">However, we may share your data with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Creem</strong> (for payment processing)</li>
                <li><strong>Daily.co</strong> (for video calls)</li>
                <li><strong>Google, Meta, TikTok, YouTube</strong> (to verify your influencer identity and display public data)</li>
                <li><strong>Legal authorities</strong> when required to comply with applicable law</li>
              </ul>
              
              <div className={cn("mt-4 p-4 rounded-lg border-l-4", "bg-yellow-50 border-yellow-400")}>
                <p className="font-medium text-yellow-800">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  Note: These third parties may use your email address or other data for their own marketing purposes 
                  if you've consented to that when signing up with them.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Data Storage & Security */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Lock} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">7. Data Storage & Security</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Lock className="h-6 w-6 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-blue-800 mb-2">Secure Storage</h4>
                  <p className="text-blue-700 text-sm">We store your data on secure cloud infrastructure</p>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <Shield className="h-6 w-6 text-green-600 mb-2" />
                  <h4 className="font-semibold text-green-800 mb-2">Encryption</h4>
                  <p className="text-green-700 text-sm">End-to-end encryption for sensitive operations like login and video calls</p>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <Users className="h-6 w-6 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-purple-800 mb-2">Limited Access</h4>
                  <p className="text-purple-700 text-sm">Access to personal data is limited to authorized personnel</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Your Rights */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Shield} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">8. Your Rights</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Depending on your location, you may have the right to:</p>
              
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Eye className="h-5 w-5 text-blue-600" />
                  <span>Access your data</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span>Correct your data</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span>Delete your account</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Database className="h-5 w-5 text-purple-600" />
                  <span>Request data portability</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-orange-600" />
                  <span>Withdraw consent for specific data use</span>
                </div>
              </div>
              
              <div className={cn("mt-6 p-4 rounded-lg", theme.accent)}>
                <p className="font-medium">
                  You can make a request by emailing us at{' '}
                  <a href="mailto:info@nexbii.com" className={cn("underline", theme.secondary)}>
                    info@nexbii.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 9: International Users */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Globe} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">9. International Users</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                Our services are available globally. If you're located outside the UK or EU, your information may be 
                transferred and processed in countries that may not have the same data protection laws.
              </p>
              <div className={cn("p-4 rounded-lg border-l-4", "bg-blue-50 border-blue-400")}>
                <p className="font-medium text-blue-800">
                  By using our platform, you consent to such transfers.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10: Policy Updates */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertCircle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">10. Updates to This Policy</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                We may update this Privacy Policy from time to time. If changes are significant, we'll notify you 
                through the app or via email.
              </p>
              <div className={cn("p-4 rounded-lg", theme.accent)}>
                <p className="font-medium">
                  Please review this page periodically to stay informed about how we protect your privacy.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Mail} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">11. Contact Us</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                If you have any questions or concerns about this Privacy Policy or how we handle your data, 
                please contact us at:
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <a href="mailto:info@nexbii.com" className={cn("font-medium", theme.secondary)}>
                    info@nexbii.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <a href="https://www.nexbii.app" target="_blank" rel="noopener noreferrer" className={cn("font-medium", theme.secondary)}>
                    www.nexbii.app
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 Nexbii. All rights reserved.</p>
          <p className="mt-2">Last updated: June 19, 2025</p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
